import { APP_CONSTANTS } from 'src/consumer/constants'
import logo_brand from 'src/assets/logo_brand.png'
import JoinSessionRelatedButtons from 'src/consumer/components/PublicLiveSessionPage/components/JoinSessionRelatedButtons'
import useElementDimensions from 'src/customHooks/customHooks'
import { markBookedSessionAsCompletedFunction, getImageLink, logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { DisplayFlex } from '../../../ConsumerProfilePage/ProfileLeftPannelStyle'
import {
  LSVideoPartContainer
} from '../../LiveSessionPageStyles'
import CameraPreview from './CameraPreview'
import { Box, Avatar, Menu, MenuItem, IconButton, Tooltip, Divider, Typography, CircularProgress } from '@mui/material'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import VideocamIcon from '@mui/icons-material/Videocam'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import { useContext, useState, useEffect, useMemo } from 'react'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import {
  GENDER_LIST_ONBOARDING,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_THERAPY_SESSION,
  BOOKING_SESSION_STATUS,
} from 'src/consumer/constants'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'
import Business from '@mui/icons-material/Business'
import { useDispatch, useSelector } from 'react-redux'
import { getTeacherClinicAssociations } from 'src/reducers/teacherProfileData/services/clinicAssociations'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getOrdinalSuffixForSuperScript, getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'
import dayjs from 'dayjs'
import PsychologyIcon from '@mui/icons-material/Psychology'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReplayIcon from '@mui/icons-material/Replay'
import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/Print'
import PatientQuickActions from 'src/shared/patientActions/PatientQuickActions'
import { mediaURL } from 'src/consumer/services'
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined'
import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import UnifiedActionsDrawer from 'src/clinic/components/ClinicManageBookingsPage/slotItem/components/UnifiedActionsDrawer'

const JoinSessionScreen = ({
  // Props from parent
  isMountedOnAWebView,
  navigate,
  bookedEventFinalData,
  userRole,
  auxiliaryData,
  showRejoin,
  timeElapsedInSeconds,

  // Device props
  availableCameras,
  availableMicrophones,
  cameraError,
  microphoneError,
  selectedCamera,
  selectedMicrophone,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
  setSelectedCamera,
  setSelectedMicrophone,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  tokenLoading,
  userHasAllowedAudio,
  userHasAllowedVideo,
  selectedPlatform,

  // Actions
  handleJoinRoom,
  handleSeeAllSessions,
  getProcessAndSetEventData,

  // Next events
  nextEvents,
  mentorEventsList,
  markCompleted,
}) => {
  const { dimensions, ref } = useElementDimensions()
  const mentorProfileContext = useContext(MentorProfileContext)
  const profileContext = useContext(ProfileContext)
  // Try to get mentor profile data from MentorProfileContext first, fallback to ProfileContext
  const mentorProfileData = mentorProfileContext?.mentorProfileData || profileContext?.teacher
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  
  // Check if user is checked-in to a room
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  
  useEffect(() => {
    const checkCheckedInStatus = () => {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      setIsCheckedIn(!!checkedInDetails)
    }
    
    // Check initially
    checkCheckedInStatus()
    
    // Listen for storage changes
    const handleStorageChange = () => {
      checkCheckedInStatus()
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    // Also check periodically in case localStorage is changed in same window
    const interval = setInterval(checkCheckedInStatus, 1000)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Get patient info for mentor view
  const prescriptionData = auxiliaryData?.metaPrescription
  const therapyReportData = auxiliaryData?.therapyReport
  const screeningReportData = auxiliaryData?.screeningReport
  const patientVisitSummary = auxiliaryData?.patientVisitSummary
  
  const patientName = prescriptionData?.patientPrefill?.name || 
                      therapyReportData?.patientName || 
                      screeningReportData?.patientName ||
                      bookedEventFinalData?.userName
  const patientDateOfBirth = prescriptionData?.patientPrefill?.vital?.dateOfBirth || 
                             therapyReportData?.patientDateOfBirth || 
                             screeningReportData?.patientDateOfBirth
  const patientGender = prescriptionData?.patientPrefill?.vital?.gender || 
                        therapyReportData?.patientGender || 
                        screeningReportData?.patientGender
  const patientNumber = patientVisitSummary?.patientNumber || 
                       prescriptionData?.patientPrefill?.patientNumber ||
                       therapyReportData?.patientNumber ||
                       screeningReportData?.patientNumber ||
                       bookedEventFinalData?.appointmentNumber
  const patientVisitNumber = bookedEventFinalData?.patientMeetingSeqNumber

  // Session type: video call or in-person
  const isVideoCall = bookedEventFinalData?.meetingSrc !== 'offline'
  const isInPerson = bookedEventFinalData?.meetingSrc === 'offline'

  // Mentor info (for showing on right side)
  const mentorName = bookedEventFinalData?.mentorName
  const mentorProfilePicId = bookedEventFinalData?.mentorProfilePicId

  // Extract summary data
  const eventType = bookedEventFinalData?.eventType
  const hasTherapyData = patientVisitSummary?.therapySessionsConducted > 0 || patientVisitSummary?.inTherapySince
  const hasConsultationData = patientVisitSummary?.referringPractitionerName

  const clinicId = bookedEventFinalData?.clinicId
  // This clinic's day at a glance: how many of today's sessions are done.
  const clinicDaySessions = useMemo(() => {
    const list = Array.isArray(mentorEventsList) ? mentorEventsList : []
    return clinicId ? list.filter((s) => s?.clinicId === clinicId) : list
  }, [mentorEventsList, clinicId])
  const totalToday = clinicDaySessions.length
  const completedToday = clinicDaySessions.filter((s) => s?.markedCompleted).length
  const dayProgressPct = totalToday > 0 ? Math.round((completedToday / totalToday) * 100) : 0

  // Clinic-brand names so sessions belonging to OTHER clinics can be labelled (like the day
  // calendar). Cached redux thunk → /api/secure/teacher/clinic-profile.
  const dispatch = useDispatch()
  const associatedClinics = useSelector((s) => s?.teacherProfileDataStore?.associatedClinics?.data)
  useEffect(() => {
    if (userRole === 'mentor' && (!Array.isArray(associatedClinics) || associatedClinics.length === 0)) {
      dispatch(getTeacherClinicAssociations())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole])
  const clinicInfoById = useMemo(() => {
    const map = {}
    ;(Array.isArray(associatedClinics) ? associatedClinics : []).forEach((c) => {
      const info = {
        brandName: c?.clinicBrandName || c?.brandName,
        locationName: c?.title || c?.name || c?.clinicLocationName,
      }
      if (c?.id) map[c.id] = info
      if (c?.clinicId) map[c.clinicId] = info
    })
    return map
  }, [associatedClinics])
  const currentClinicInfo = clinicId ? clinicInfoById[clinicId] : null
  const clinicName =
    currentClinicInfo?.locationName ||
    bookedEventFinalData?.clinicLocation?.title ||
    bookedEventFinalData?.clinicLocation?.name ||
    bookedEventFinalData?.clinicLocation?.clinicLocationName ||
    bookedEventFinalData?.clinicLocationTitle ||
    bookedEventFinalData?.locationName ||
    bookedEventFinalData?.clinicLocationName ||
    patientVisitSummary?.clinicLocationTitle ||
    patientVisitSummary?.clinicLocationName ||
    'Clinic location'

  // Mentor prescription-image endpoints (same as the mentor calendar/chamber) so the slot
  // strip's upload + more-options work on this screen too.
  const PRESCRIPTION_IMAGES_GET_API = '/api/secure/mentor/emr-reports/prescription/images/get-by-eventId'
  const PRESCRIPTION_IMAGES_SAVE_API = '/api/secure/mentor/emr-reports/prescription/images/save'

  // One-line patient meta for a session row: "36 / F · #HRM00156 · 2nd visit".
  // (Time + mode are shown separately in the card's left block.)
  const sessionMetaLine = (slot) => {
    const dob = slot?.patientInfo?.dateOfBirth
    const age = dob
      ? getAgeFromDobWithTime(dob)
      : slot?.patientInfo?.age != null
      ? `${slot.patientInfo.age}Y`
      : null
    const genderVal = slot?.patientInfo?.gender
    const gender = genderVal
      ? GENDER_LIST_ONBOARDING?.find((g) => g.value === genderVal)?.label || genderVal
      : null
    const apptNum = slot?.appointmentNumber
    const visit = slot?.patientMeetingSeqNumber
    return [
      [age, gender].filter(Boolean).join(' / ') || null,
      apptNum ? `#${apptNum}` : null,
      visit ? `${visit}${getOrdinalSuffixForSuperScript(visit)} visit` : null,
    ]
      .filter(Boolean)
      .join(' · ')
  }

  // Shared "more options" / upload drawer for a session card. Reuses the calendar's
  // UnifiedActionsDrawer (upload prescription, vitals, joining details, …) so the actions
  // work here without embedding the busy calendar strip in each card.
  const [actionSlot, setActionSlot] = useState(null)
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false)
  const [actionDrawerTab, setActionDrawerTab] = useState('uploadPrescription')

  const getActionTabsForSlot = (slot) => {
    const tabs = []
    if (slot?.meetingSrc && slot.meetingSrc !== 'offline') tabs.push('joiningDetails')
    if (slot?.eventType === EVENT_TYPE_CONSULTATION) tabs.push('uploadPrescription')
    tabs.push('addVitals')
    return tabs
  }

  const openSlotActions = (slot, tab) => {
    setActionSlot(slot)
    setActionDrawerTab(tab || getActionTabsForSlot(slot)[0] || 'uploadPrescription')
    setActionDrawerOpen(true)
  }

  // Current (just-completed) session as a slot-shaped object so it renders like the others.
  const currentSlot =
    (Array.isArray(mentorEventsList) ? mentorEventsList : []).find(
      (s) => s?.id === bookedEventFinalData?.id,
    ) || {
      ...(bookedEventFinalData || {}),
      patientInfo: { name: patientName, dateOfBirth: patientDateOfBirth, gender: patientGender },
      appointmentNumber: patientNumber,
      patientMeetingSeqNumber: patientVisitNumber,
    }

  // Clean session card (used for both the just-completed and upcoming sessions).
  const renderSessionRow = (slot, { completed = false } = {}) => {
    if (!slot) return null
    const name = slot?.patientInfo?.name || slot?.userName || slot?.patientName || patientName || 'Patient'
    const tabs = getActionTabsForSlot(slot)
    const meetingSrc = slot?.meetingSrc
    const modeLabel = getMeetingSrcLabel(meetingSrc)
    const isInProgress = slot?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS
    const isOtherClinic = Boolean(clinicId && slot?.clinicId && slot.clinicId !== clinicId)
    const otherClinicInfo = isOtherClinic ? clinicInfoById[slot?.clinicId] : null
    const startLocal = slot?.startTime ? dayjs.utc(slot.startTime).local() : null
    const timeMain = startLocal ? startLocal.format('h:mm') : '--'
    const timeMeridiem = startLocal ? startLocal.format('A') : ''

    // State-based theme matching the day-calendar colours.
    let accent = '#6366f1' // default scheduled — indigo
    let cardBg = '#ffffff'
    let cardBorder = '1px solid #e5e7eb'
    if (completed) {
      accent = '#10b981' // completed — green
      cardBg = '#ecfdf5'
      cardBorder = '1px solid #6ee7b7'
    } else if (isInProgress) {
      accent = '#f59e0b' // in progress — amber/yellow
      cardBg = '#fffbeb'
      cardBorder = '1px solid #fcd34d'
    }
    if (isOtherClinic) {
      accent = '#8b5cf6' // other clinic — purple
      cardBorder = '2px solid #8b5cf6'
      cardBg = '#f5f3ff'
    }

    const iconBtnSx = {
      width: 30,
      height: 30,
      color: '#534AB7',
      border: '1px solid #CECBF6',
      borderRadius: '8px',
      backgroundColor: '#fff',
      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.08)' },
    }
    const openSession = async () => {
      if (completed) {
        handleJoinRoom()
        return
      }
      if (!markCompleted) {
        await markBookedSessionAsCompletedFunction(bookedEventFinalData)
      }
      navigate(`/l/${slot?.slug}`)
      window.location.reload()
    }
    return (
      <Box
        sx={{
          border: cardBorder,
          background: cardBg,
          borderRadius: '12px',
          padding: '10px 12px',
          ...(isOtherClinic && {
            // Diagonal stripes for sessions belonging to another clinic (same as day calendar).
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(139, 92, 246, 0.08) 10px, rgba(139, 92, 246, 0.08) 20px)',
            backgroundColor: '#f5f3ff',
          }),
        }}
      >
        {completed && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#047857',
              backgroundColor: '#a7f3d0',
              padding: '2px 8px',
              borderRadius: '20px',
              mb: '6px',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 13 }} /> Just completed
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '10px' }}>
          <Box
            onClick={openSession}
            role="button"
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, cursor: 'pointer' }}
          >
            {/* Left block: session time + in-person / video mode, in a contrasting colour. */}
            <Box
              sx={{
                width: 50,
                minWidth: 50,
                alignSelf: 'stretch',
                borderRadius: '10px',
                background: accent,
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                padding: '6px 2px',
                flexShrink: 0,
              }}
            >
              {getMeetingSrcIcon(meetingSrc, { size: 15, color: '#fff' })}
              <Box sx={{ textAlign: 'center', lineHeight: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', lineHeight: 1.05 }}>
                  {timeMain}
                </Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1 }}>
                  {timeMeridiem}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: '13px', md: '14px' },
                  fontWeight: 600,
                  color: '#111827',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#6b7280',
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {sessionMetaLine(slot)}
                {modeLabel ? ` · ${modeLabel}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {tabs.includes('uploadPrescription') && (
              <Tooltip title="Upload prescription" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    openSlotActions(slot, 'uploadPrescription')
                  }}
                  sx={iconBtnSx}
                >
                  <FileUploadOutlined sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="More options" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  openSlotActions(slot)
                }}
                sx={iconBtnSx}
              >
                <AddCircleOutline sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {completed && (
          <Typography sx={{ fontSize: '12px', color: '#059669', mt: '6px' }}>
            Tap to rejoin this consultation
          </Typography>
        )}
        {isOtherClinic && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              mt: '8px',
              pt: '6px',
              borderTop: '1px dashed rgba(139, 92, 246, 0.35)',
            }}
          >
            <Business sx={{ fontSize: 13, color: '#7c3aed' }} />
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#7c3aed',
                letterSpacing: '0.2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {otherClinicInfo?.brandName || 'Another clinic'}
              {otherClinicInfo?.locationName ? ` · ${otherClinicInfo.locationName}` : ''}
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  // Format date helper
  const formatDateCompact = (date) => {
    if (!date) return null
    return dayjs(date).format('DD MMM YYYY')
  }

  const getRelativeTime = (date) => {
    if (!date) return null
    return getExactDiffFromCurrentTimeDayJs(dayjs(date), dayjs(), true)
  }

  // Completed-screen prescription actions. The live Quick Print isn't available here (the
  // prescription editor isn't mounted on the completed screen), so Download/Print both use the
  // saved server-generated prescription PDF — Download saves it, Print opens the print dialog.
  const [completedPdfBusy, setCompletedPdfBusy] = useState(null) // 'download' | 'print' | null

  const generateCompletedPdfUrl = async () => {
    const { generatePrescriptionPDF } = await import(
      'src/mentor/components/PrescriptionMakingPage/prescriptionHelperFunctions'
    )
    const data = await generatePrescriptionPDF({ bookedEventId: bookedEventFinalData?.id })
    return data?.pdfMediaId ? `${mediaURL}${data.pdfMediaId}` : null
  }

  const handleCompletedDownloadPdf = async () => {
    if (completedPdfBusy) return
    setCompletedPdfBusy('download')
    try {
      const { downloadPDFFromURL } = await import(
        'src/mentor/components/PrescriptionMakingPage/prescriptionHelperFunctions'
      )
      const url = await generateCompletedPdfUrl()
      if (url) {
        await downloadPDFFromURL(url, `prescription_${patientNumber || ''}_${dayjs().format('YYYY-MM-DD')}.pdf`)
      }
    } catch (e) {
      // non-fatal
    }
    setCompletedPdfBusy(null)
  }

  const handleCompletedPrint = async () => {
    if (completedPdfBusy) return
    setCompletedPdfBusy('print')
    try {
      const url = await generateCompletedPdfUrl()
      if (url) {
        // Load the PDF in a hidden iframe and open the print dialog; fall back to a new tab.
        const iframe = document.createElement('iframe')
        Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' })
        iframe.src = url
        iframe.onload = () => {
          try {
            iframe.contentWindow.focus()
            iframe.contentWindow.print()
          } catch (e) {
            window.open(url, '_blank')
          }
        }
        document.body.appendChild(iframe)
      }
    } catch (e) {
      // non-fatal
    }
    setCompletedPdfBusy(null)
  }

  return (
    <>
      {/* Header for webview */}
      {isMountedOnAWebView && (
        <DisplayFlex>
          <div onClick={() => navigate('/webview/my-live-sessions')}>
            <u>Back to Booked Sessions</u>
          </div>
        </DisplayFlex>
      )}

      {/* Modern Minimal Header */}
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: '#ffffff',
          zIndex: 9999,
          padding: { xs: '10px 16px', md: '12px 24px' },
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          minHeight: { xs: '56px', md: '64px' },
          borderBottomLeftRadius: { xs: '12px', md: '16px' },
          borderBottomRightRadius: { xs: '12px', md: '16px' },
        }}
      >
        {/* Left side: AUMHUM Logo + Patient Info (for mentor) or Session Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, flex: 1, minWidth: 0 }}>
          {/* AUMHUM Logo */}
          <Box
            component="img"
            src={APP_CONSTANTS.APP_LOGO_LINK}
            alt="AUMHUM"
            onError={(e) => {
              e.target.src = logo_brand
            }}
            onClick={() => {
              if (isMountedOnAWebView) {
                // Do nothing for webview
              } else {
                userRole === 'mentor' ? navigate('/mentor-home') : navigate('/')
              }
            }}
            sx={{
              height: { xs: '32px', md: '36px' },
              width: 'auto',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              borderRadius: '8px',
              '&:hover': {
                transform: 'scale(1.05)',
                opacity: 0.9,
              },
            }}
          />
          
          {/* Patient identity now lives in a dedicated summary card below (slim-header
              layout). The header stays minimal; public (non-mentor) viewers still
              see the session info branch. */}
          {userRole === 'mentor' && patientName ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, minWidth: 0, flex: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '15px', md: '16px' },
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {bookedEventFinalData?.eventName}
                  </Typography>
                  {/* Session Type Icon */}
                  {isVideoCall && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#10b981',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#10b981',
                        }}
                      >
                        Video
                      </Typography>
                    </Box>
                  )}
                  {isInPerson && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#f59e0b',
                        fontSize: { xs: '12px', md: '13px' },
                        flexShrink: 0,
                      }}
                    >
                      <PersonPinIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', md: '13px' },
                          fontWeight: 500,
                          color: '#f59e0b',
                        }}
                      >
                        In-Person
                      </Typography>
                    </Box>
                  )}
                </Box>
                {bookedEventFinalData?.meetingSrc !== 'offline' && (
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', md: '13px' },
                      color: '#6b7280',
                      lineHeight: 1.2,
                      mt: 0.5,
                    }}
                  >
                    {dayjs?.utc(bookedEventFinalData?.startTime).local()?.format('MMM DD, YYYY')} •{' '}
                    {dayjs?.utc(bookedEventFinalData?.startTime).local()?.format('h:mma')} -{' '}
                    {dayjs?.utc(bookedEventFinalData?.endTime).local()?.format('h:mma')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Right side: Checkout Button (if checked-in) or Mentor Profile */}
        {userRole === 'mentor' && isCheckedIn ? (
          <CheckoutButtonComponent
            text="Check out"
            styleProp={{
              fontSize: { xs: '13px', md: '14px' },
              fontWeight: 500,
              padding: { xs: '6px 14px', md: '8px 18px' },
              // De-emphasised: check-out is an end-of-visit action, not the pre-join focus.
              backgroundColor: 'transparent',
              color: '#dc2626',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              borderRadius: '8px',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.06)',
                borderColor: 'rgba(239, 68, 68, 0.55)',
                boxShadow: 'none',
              },
            }}
            buttonSize="medium"
            buttonVariant="outlined"
          />
        ) : userRole === 'mentor' ? (
          <Tooltip title="Account Menu" arrow>
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{
                padding: '4px',
                borderRadius: '12px',
                border: '2px solid rgba(99, 102, 241, 0.2)',
                backgroundColor: 'rgba(99, 102, 241, 0.04)',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.12)',
                  borderColor: 'rgba(99, 102, 241, 0.4)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                src={
                  mentorProfileData?.profilePicId
                    ? getImageLink(mentorProfileData.profilePicId)
                    : profileContext?.teacher?.profilePicId
                    ? getImageLink(profileContext.teacher.profilePicId)
                    : mentorProfilePicId
                    ? getImageLink(mentorProfilePicId)
                    : undefined
                }
                sx={{
                  width: { xs: 32, md: 36 },
                  height: { xs: 32, md: 36 },
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
                  background:
                    mentorProfileData?.profilePicId || profileContext?.teacher?.profilePicId || mentorProfilePicId
                      ? undefined
                      : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {mentorProfileData?.name?.charAt(0)?.toUpperCase() || 
                 profileContext?.teacher?.name?.charAt(0)?.toUpperCase() || 
                 mentorName?.charAt(0)?.toUpperCase() || 
                 'M'}
              </Avatar>
            </IconButton>
          </Tooltip>
        ) : mentorName ? (
          <Avatar
            src={mentorProfilePicId ? getImageLink(mentorProfilePicId) : undefined}
            sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              border: '2px solid rgba(99, 102, 241, 0.2)',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
              background: mentorProfilePicId ? undefined : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: { xs: '14px', md: '16px' },
            }}
          >
            {mentorName?.charAt(0)?.toUpperCase() || 'M'}
          </Avatar>
        ) : null}

        {/* Dropdown Menu - Always render when user is mentor, controlled by open state */}
        {userRole === 'mentor' && (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            disableScrollLock={false}
            MenuListProps={{
              'aria-labelledby': 'account-menu-button',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                zIndex: 10000,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              root: {
                sx: {
                  zIndex: 10000,
                },
              },
            }}
          >
            {/* User Info Section */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                backgroundColor: 'rgba(99, 102, 241, 0.02)',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'rgba(15, 23, 42, 0.95)',
                  fontSize: '14px',
                  mb: 0.5,
                }}
              >
                {mentorProfileData?.name || profileContext?.teacher?.name || profileContext?.name || 'Mentor'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(100, 116, 139, 0.8)',
                  fontSize: '12px',
                }}
              >
                Mentor Portal
              </Typography>
            </Box>

            {/* Profile Menu Item */}
            <MenuItem
              onClick={() => {
                navigate('/mentor-home/profile')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(71, 85, 105, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  color: 'rgba(99, 102, 241, 0.95)',
                },
              }}
            >
              <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(99, 102, 241, 0.7)' }} />
              Profile Settings
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* Logout */}
            <MenuItem
              onClick={() => {
                logoutHandlerMentor()
                navigate('/')
                handleClose()
              }}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                color: 'rgba(239, 68, 68, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  color: 'rgba(239, 68, 68, 1)',
                },
              }}
            >
              <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
              Sign Out
            </MenuItem>
          </Menu>
        )}
      </Box>

      {/* Main Join Area */}
      <LSVideoPartContainer
        style={{
          flexDirection: 'column',
          // Top-align the join content. The base style centers it vertically, which on a tall
          // mobile screen leaves a large confusing empty gap between the header and the content.
          justifyContent: 'flex-start',
          gap: 0,
          flexGrow: 1,
          padding: 0,
          minHeight: `calc(100vh - ${dimensions?.height}px)`,
          overflowY: 'auto',
          overflowX: 'hidden',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 50%, #f0f2f5 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '16px', md: '24px' },
            padding: { xs: '16px', md: '32px' },
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
            // border-box so the horizontal padding is included in width:100% — otherwise the
            // content box renders 40px wider than the viewport and the cards/button get clipped.
            boxSizing: 'border-box',
            alignItems: 'center',
          }}
        >
          {/* Clinic day-progress card (completed screen) — clinic name + how many of today's
              sessions are done, so the practitioner sees their progress at a glance. */}
          {showRejoin && userRole === 'mentor' && totalToday > 0 && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '980px' },
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: { xs: '14px', md: '16px' },
                padding: { xs: '12px 14px', md: '14px 18px' },
                boxSizing: 'border-box',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 19, color: '#6366f1' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '15px' },
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {clinicName || 'Today'}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: '1px' }}>
                    {totalToday} patient{totalToday !== 1 ? 's' : ''} today
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: { xs: '15px', md: '16px' }, fontWeight: 600, color: '#047857', lineHeight: 1.1 }}>
                    {completedToday} of {totalToday}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>completed</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: '10px', height: '6px', borderRadius: '6px', backgroundColor: '#E1F5EE', overflow: 'hidden' }}>
                <Box sx={{ width: `${dayProgressPct}%`, height: '100%', backgroundColor: '#1D9E75', transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          )}

          {/* Patient summary — moved out of the header into a dedicated, scannable card.
              On the completed/rejoin screen this is replaced by the fused patient + actions card. */}
          {userRole === 'mentor' && patientName && !showRejoin && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '800px' },
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: { xs: '14px', md: '16px' },
                padding: { xs: '14px', md: '18px' },
                boxSizing: 'border-box',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '12px', md: '14px' } }}>
                <Box
                  sx={{
                    width: { xs: '44px', md: '48px' },
                    height: { xs: '44px', md: '48px' },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: { xs: '18px', md: '20px' },
                    flexShrink: 0,
                  }}
                >
                  {patientName?.charAt(0)?.toUpperCase() || 'P'}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', md: '18px' },
                      fontWeight: 600,
                      color: '#111827',
                      lineHeight: 1.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {patientName}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: { xs: '10px 12px', md: '12px 16px' },
                  marginTop: { xs: '14px', md: '16px' },
                  paddingTop: { xs: '14px', md: '16px' },
                  borderTop: '1px solid #f1f1ee',
                }}
              >
                {(patientDateOfBirth || patientGender) && (
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: '2px' }}>Age / Sex</Typography>
                    <Typography sx={{ fontSize: { xs: '13px', md: '14px' }, color: '#111827', fontWeight: 500 }}>
                      {patientDateOfBirth ? getAgeFromDobWithTime(patientDateOfBirth) : ''}
                      {patientDateOfBirth && patientGender ? ' / ' : ''}
                      {patientGender
                        ? GENDER_LIST_ONBOARDING?.find((item) => item.value === patientGender)?.label
                        : ''}
                    </Typography>
                  </Box>
                )}
                {patientNumber && (
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: '2px' }}>Patient ID</Typography>
                    <Typography sx={{ fontSize: { xs: '13px', md: '14px' }, color: '#111827', fontWeight: 500 }}>
                      #{patientNumber}
                    </Typography>
                  </Box>
                )}
                {patientVisitNumber && (
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: '2px' }}>Visit</Typography>
                    <Typography sx={{ fontSize: { xs: '13px', md: '14px' }, color: '#111827', fontWeight: 500 }}>
                      {patientVisitNumber}
                      <sup style={{ fontSize: '9px', marginLeft: '1px' }}>
                        {getOrdinalSuffixForSuperScript(patientVisitNumber)}
                      </sup>
                    </Typography>
                  </Box>
                )}
                {(isInPerson || isVideoCall) && (
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', mb: '2px' }}>Mode</Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '13px', md: '14px' },
                        fontWeight: 500,
                        color: isInPerson ? '#b45309' : '#047857',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {isInPerson ? (
                        <PersonPinIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <VideocamIcon sx={{ fontSize: 16 }} />
                      )}
                      {isInPerson ? 'In-person' : 'Video'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Therapy/Consultation Summary - Compact Display. Hidden on the completed/rejoin
              screen to keep that view focused on actions + the sessions list. */}
          {!showRejoin &&
            ((eventType === EVENT_TYPE_CONSULTATION && hasTherapyData) ||
              (eventType === EVENT_TYPE_THERAPY_SESSION && hasConsultationData)) && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '800px' },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '12px', md: '16px' },
              }}
            >
              {/* Therapy Summary (shown for consultations) */}
              {eventType === EVENT_TYPE_CONSULTATION && hasTherapyData && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    borderRadius: '16px',
                    padding: { xs: '16px', md: '20px' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: '12px', md: '16px' },
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                      borderRadius: '12px',
                      padding: { xs: '10px', md: '12px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PsychologyIcon sx={{ fontSize: { xs: '20px', md: '24px' }, color: '#6366f1' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '11px', md: '12px' },
                        fontWeight: 700,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: '#6366f1',
                        mb: '2px',
                      }}
                    >
                      For your context
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '14px', md: '15px' },
                        fontWeight: 600,
                        color: '#111827',
                        mb: { xs: '6px', md: '8px' },
                      }}
                    >
                      Patient&apos;s ongoing therapy
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '4px', md: '6px' } }}>
                      {patientVisitSummary?.referringPractitionerName && (
                        <Typography
                          sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          In therapy with{' '}
                          <Box component="strong" sx={{ fontWeight: 600 }}>
                            {patientVisitSummary.referringPractitionerName}
                          </Box>
                        </Typography>
                      )}
                      {patientVisitSummary?.therapySessionsConducted > 0 && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          {patientVisitSummary.therapySessionsConducted} session{patientVisitSummary.therapySessionsConducted !== 1 ? 's' : ''} completed
                        </Typography>
                      )}
                      {patientVisitSummary?.inTherapySince && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          Started {formatDateCompact(patientVisitSummary.inTherapySince)} ({getRelativeTime(patientVisitSummary.inTherapySince)})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Consultation Summary (shown for therapy sessions) */}
              {eventType === EVENT_TYPE_THERAPY_SESSION && hasConsultationData && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                    borderRadius: '16px',
                    padding: { xs: '16px', md: '20px' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: '12px', md: '16px' },
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                      borderRadius: '12px',
                      padding: { xs: '10px', md: '12px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocalHospitalIcon sx={{ fontSize: { xs: '20px', md: '24px' }, color: '#10b981' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '11px', md: '12px' },
                        fontWeight: 700,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: '#10b981',
                        mb: '2px',
                      }}
                    >
                      For your context
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '14px', md: '15px' },
                        fontWeight: 600,
                        color: '#111827',
                        mb: { xs: '6px', md: '8px' },
                      }}
                    >
                      Patient&apos;s doctor consultation
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '4px', md: '6px' } }}>
                      {patientVisitSummary?.referringPractitionerName && (
                        <Typography
                          sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          Referring Doctor:{' '}
                          <Box component="strong" sx={{ fontWeight: 600 }}>
                            {patientVisitSummary.referringPractitionerName}
                          </Box>
                        </Typography>
                      )}
                      {patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor !== undefined && (
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '13px' },
                            color: '#6b7280',
                          }}
                        >
                          {patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor + 1}
                          <sup style={{ fontSize: '10px' }}>
                            {getOrdinalSuffixForSuperScript(patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor + 1)}
                          </sup>{' '}
                          therapy session
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Completed-session layout (mentor): upcoming sessions on the left, session
              actions on the right. Stacks to actions-first on mobile to minimise scrolling. */}
          {showRejoin && userRole === 'mentor' ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '980px' },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '16px', md: '20px' },
                alignItems: 'stretch',
              }}
            >
              {/* Left — today's sessions; the just-completed one is clearly badged */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '1 1 0%' },
                  minWidth: 0,
                  order: { xs: 2, md: 1 },
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: { xs: '14px', md: '16px' },
                  padding: { xs: '14px', md: '18px' },
                  boxSizing: 'border-box',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: '10px',
                    mb: '12px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarMonthIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                    <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, fontWeight: 600, color: '#111827' }}>
                      Today&apos;s sessions
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    onClick={handleSeeAllSessions}
                    aria-label="See all today's sessions"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '7px',
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      border: '1px solid #5145cd',
                      background: 'linear-gradient(135deg, #6d5dfc 0%, #5145cd 100%)',
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontSize: { xs: '13px', md: '14px' },
                      fontWeight: 700,
                      minHeight: '38px',
                      padding: { xs: '9px 12px', sm: '8px 13px' },
                      borderRadius: '10px',
                      boxShadow: '0 8px 18px rgba(81, 69, 205, 0.24)',
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        boxShadow: '0 10px 22px rgba(81, 69, 205, 0.3)',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-visible': {
                        outline: '3px solid rgba(99, 102, 241, 0.28)',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    See all sessions
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '22px',
                        height: '22px',
                        padding: '0 6px',
                        borderRadius: '999px',
                        backgroundColor: 'rgba(255, 255, 255, 0.18)',
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {totalToday}
                    </Box>
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Just-completed (current) session - visually distinct, tap to rejoin */}
                  {renderSessionRow(currentSlot, { completed: true })}

                  {/* Upcoming sessions - clean cards with name/age/gender + per-card
                      upload & more-options actions (shared UnifiedActionsDrawer below). */}
                  {nextEvents && nextEvents.length > 0 ? (
                    nextEvents.map((nextEvent, nextEventIndex) => (
                      <Box key={nextEvent?.id || nextEventIndex}>{renderSessionRow(nextEvent)}</Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        py: { xs: '16px', md: '24px' },
                      }}
                    >
                      <EventAvailableIcon sx={{ fontSize: 28, color: '#d1d5db', mb: '6px' }} />
                      <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                        No upcoming sessions — you&apos;re all caught up.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Right — fused patient + actions card */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '0 0 320px' },
                  width: { xs: '100%', md: '320px' },
                  order: { xs: 1, md: 2 },
                  alignSelf: 'flex-start',
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: { xs: '14px', md: '16px' },
                  padding: { xs: '16px', md: '18px' },
                  boxSizing: 'border-box',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    color: '#059669',
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: '12px',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 16 }} /> Consultation completed
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '10px' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '18px',
                      flexShrink: 0,
                    }}
                  >
                    {patientName?.charAt(0)?.toUpperCase() || 'P'}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#111827',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {patientName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: '2px' }}>
                      {[
                        patientDateOfBirth ? getAgeFromDobWithTime(patientDateOfBirth) : null,
                        patientGender
                          ? GENDER_LIST_ONBOARDING?.find((i) => i.value === patientGender)?.label
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' / ')}
                      {patientNumber ? ` \u00b7 #${patientNumber}` : ''}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '14px' }}>
                  {patientVisitNumber && (
                    <Box sx={{ fontSize: '11px', color: '#8b5cf6', backgroundColor: 'rgba(139, 92, 246, 0.1)', padding: '2px 8px', borderRadius: '16px' }}>
                      {patientVisitNumber}
                      <sup style={{ fontSize: '8px', marginLeft: '1px' }}>
                        {getOrdinalSuffixForSuperScript(patientVisitNumber)}
                      </sup>{' '}
                      visit
                    </Box>
                  )}
                  {(isInPerson || isVideoCall) && (
                    <Box
                      sx={{
                        fontSize: '11px',
                        color: isInPerson ? '#b45309' : '#047857',
                        backgroundColor: isInPerson ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                        padding: '2px 8px',
                        borderRadius: '16px',
                      }}
                    >
                      {isInPerson ? 'In-person' : 'Video'}
                    </Box>
                  )}
                </Box>

                <Box
                  component="button"
                  onClick={(e) => handleJoinRoom(e)}
                  disabled={tokenLoading}
                  sx={{
                    width: '100%',
                    background: tokenLoading ? '#9ca3af' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '13px 16px',
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: tokenLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover:not(:disabled)': { boxShadow: '0 6px 16px rgba(139, 92, 246, 0.4)' },
                  }}
                >
                  {tokenLoading ? (
                    <CircularProgress sx={{ height: '20px !important', width: '20px !important', color: '#fff' }} />
                  ) : (
                    <ReplayIcon sx={{ fontSize: 18 }} />
                  )}
                  Rejoin consultation
                </Box>

                <Box sx={{ display: 'flex', gap: '8px', mt: '10px' }}>
                  <Box
                    component="button"
                    onClick={handleCompletedDownloadPdf}
                    disabled={!!completedPdfBusy}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px',
                      border: '1px solid #d1d5db',
                      background: '#fff',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      color: '#4b5563',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: completedPdfBusy ? 'not-allowed' : 'pointer',
                      '&:hover:not(:disabled)': { background: '#f9fafb', borderColor: '#9ca3af' },
                    }}
                  >
                    {completedPdfBusy === 'download' ? (
                      <CircularProgress sx={{ height: '18px !important', width: '18px !important', color: '#6b7280' }} />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    )}
                    Download
                  </Box>
                  <Box
                    component="button"
                    onClick={handleCompletedPrint}
                    disabled={!!completedPdfBusy}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px',
                      border: '1px solid #d1d5db',
                      background: '#fff',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      color: '#4b5563',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: completedPdfBusy ? 'not-allowed' : 'pointer',
                      '&:hover:not(:disabled)': { background: '#f9fafb', borderColor: '#9ca3af' },
                    }}
                  >
                    {completedPdfBusy === 'print' ? (
                      <CircularProgress sx={{ height: '18px !important', width: '18px !important', color: '#6b7280' }} />
                    ) : (
                      <PrintIcon sx={{ fontSize: 18 }} />
                    )}
                    Print
                  </Box>
                </Box>
                <PatientQuickActions
                  contactContext={{
                    source: 'mentor-session',
                    bookedEventId: bookedEventFinalData?.id,
                    clinicBrandId: bookedEventFinalData?.clinicBrandId,
                    clinicId: bookedEventFinalData?.clinicId,
                    patientName,
                  }}
                  layout="stack"
                  dense
                  sx={{ mt: '10px' }}
                />
              </Box>
            </Box>
          ) : selectedPlatform?.id === 'aumhum' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '24px', md: '32px' },
                width: '100%',
                maxWidth: '1000px',
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              {/* Camera Preview - Left side on desktop, top on mobile */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '0 0 400px' },
                  width: { xs: '100%', md: '400px' },
                }}
              >
                <CameraPreview
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  userHasAllowedVideo={userHasAllowedVideo}
                  userHasAllowedAudio={userHasAllowedAudio}
                  isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
                  meetingSrc={bookedEventFinalData?.meetingSrc}
                  microphoneError={microphoneError}
                  cameraError={cameraError}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                />
              </Box>

              {/* Join Session Controls - Right side on desktop, bottom on mobile */}
              <Box
                sx={{
                  flex: { xs: 'none', md: '1' },
                  width: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '24px', md: '24px' },
                }}
              >
                <JoinSessionRelatedButtons
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  cameraError={cameraError}
                  handleJoinRoom={handleJoinRoom}
                  handleSeeAllSessions={handleSeeAllSessions}
                  microphoneError={microphoneError}
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  tokenLoading={tokenLoading}
                  userHasAllowedAudio={userHasAllowedAudio}
                  userHasAllowedVideo={userHasAllowedVideo}
                  isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
                  showTermsText={bookedEventFinalData?.eventType !== 'consultation'}
                  showRejoin={showRejoin}
                  bookedEventData={bookedEventFinalData}
                  auxiliaryData={auxiliaryData}
                  showPdfRelatedActions={bookedEventFinalData?.eventType === 'consultation'}
                  hideDeviceSelection={true}
                />
              </Box>
            </Box>
          ) : (
            /* For non-video call sessions, show controls normally */
            <JoinSessionRelatedButtons
              availableCameras={availableCameras}
              availableMicrophones={availableMicrophones}
              cameraError={cameraError}
              handleJoinRoom={handleJoinRoom}
              handleSeeAllSessions={handleSeeAllSessions}
              microphoneError={microphoneError}
              selectedCamera={selectedCamera}
              selectedMicrophone={selectedMicrophone}
              setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
              setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
              setSelectedCamera={setSelectedCamera}
              setSelectedMicrophone={setSelectedMicrophone}
              setUserHasAllowedAudio={setUserHasAllowedAudio}
              setUserHasAllowedVideo={setUserHasAllowedVideo}
              tokenLoading={tokenLoading}
              userHasAllowedAudio={userHasAllowedAudio}
              userHasAllowedVideo={userHasAllowedVideo}
              isVideoCallScreen={selectedPlatform?.id === 'aumhum'}
              showTermsText={bookedEventFinalData?.eventType !== 'consultation'}
              showRejoin={showRejoin}
              bookedEventData={bookedEventFinalData}
              auxiliaryData={auxiliaryData}
              showPdfRelatedActions={bookedEventFinalData?.eventType === 'consultation'}
            />
          )}

          {/* Mark Complete Button for Mentors */}
          {/* {showRejoin && userRole === 'mentor' && !Boolean(markCompleted) && (
          <div
            onClick={async () => {
              await markBookedSessionAsCompletedFunction(bookedEventFinalData)
              await getProcessAndSetEventData()
            }}
            style={{ textAlign: 'center', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Mark Complete
          </div>
        )} */}

          {/* Upcoming sessions now live in the left column of the completed-session layout above. */}
        </Box>
      </LSVideoPartContainer>

      {/* Shared actions drawer (upload prescription / vitals / joining details) for the
          session cards on the completed screen — reuses the day-calendar's drawer. */}
      {actionSlot && (
        <UnifiedActionsDrawer
          open={actionDrawerOpen}
          onClose={() => setActionDrawerOpen(false)}
          initialTab={actionDrawerTab}
          availableTabs={getActionTabsForSlot(actionSlot)}
          bookedSlot={actionSlot}
          mentorProfileData={mentorProfileData}
          forMentor
          refreshBookedSlots={() => {}}
          apiSignatures={{
            prescriptionImagesGetApiSignature: PRESCRIPTION_IMAGES_GET_API,
            prescriptionImagesSaveApiSignature: PRESCRIPTION_IMAGES_SAVE_API,
          }}
          dialogSettings={{}}
        />
      )}
    </>
  )
}

export default JoinSessionScreen
